// theme-provider.tsx — Sprint 019 + 020: Executive Theme Engine™ + Typography Engine™
//
// RESPONSIBILITIES:
//   1. Reads full AppearanceConfig from localStorage on mount.
//   2. Applies all CSS variable overrides to document.documentElement instantly.
//   3. Loads required Google Fonts dynamically (only fonts needed for active style).
//   4. Exposes full appearance state + individual setters via React context.
//
// ARCHITECTURE: Pure presentation concern.
//   All business engines (Mission Director, Execution Engine, Outcome Engine,
//   Operations Engine) remain completely unaware of themes or typography.

import {
  createContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  THEMES,
  FONT_STYLES,
  THEME_TYPOGRAPHY,
  DEFAULT_APPEARANCE,
  TOKEN_TO_CSS_VAR,
  TYPOGRAPHY_CSS_VARS,
  CORNER_RADIUS_MAP,
  FONT_SIZE_MAP,
  resolveTypography,
  extractFontNames,
  type ThemeId,
  type FontStyleId,
  type FontSize,
  type UIDensity,
  type CornerStyle,
  type AnimationLevel,
  type AppearanceConfig,
  type ThemeDefinition,
} from '@/lib/theme-engine';

// ─── Storage ──────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'nomad-appearance-v1';
const LEGACY_KEY  = 'nomad-theme-v1';

function loadConfig(): AppearanceConfig {
  try {
    // Dev-only: ?theme=X query param forces a theme for QA screenshots (never persisted)
    const urlTheme = new URLSearchParams(window.location.search).get('theme') as ThemeId | null;
    if (urlTheme && THEMES[urlTheme]) {
      return { ...DEFAULT_APPEARANCE, themeId: urlTheme };
    }
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<AppearanceConfig>;
      return { ...DEFAULT_APPEARANCE, ...parsed };
    }
    // Migrate from Sprint 019 single-key storage
    const legacyTheme = localStorage.getItem(LEGACY_KEY) as ThemeId | null;
    if (legacyTheme && THEMES[legacyTheme]) {
      return { ...DEFAULT_APPEARANCE, themeId: legacyTheme };
    }
  } catch { /* storage unavailable */ }
  return DEFAULT_APPEARANCE;
}

function saveConfig(config: AppearanceConfig): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(config)); } catch { /* storage unavailable */ }
}

// ─── Google Fonts loader ──────────────────────────────────────────────────────

const loadedFonts = new Set<string>();

function loadGoogleFont(familyName: string): void {
  if (loadedFonts.has(familyName)) return;
  loadedFonts.add(familyName);
  const encoded = familyName.replace(/\s+/g, '+');
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encoded}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

function loadFontsForConfig(config: AppearanceConfig): void {
  const typography = resolveTypography(config.themeId, config.fontStyleId);
  for (const name of extractFontNames(typography)) {
    loadGoogleFont(name);
  }
  // Also pre-load fonts for the active font style preset if not default
  if (config.fontStyleId !== 'default') {
    for (const name of FONT_STYLES[config.fontStyleId].googleFonts) {
      loadGoogleFont(name);
    }
  }
}

// ─── DOM application ──────────────────────────────────────────────────────────

function applyConfigToDom(config: AppearanceConfig): void {
  const root = document.documentElement;
  const theme = THEMES[config.themeId];

  // 0a. color-scheme — MUST be set before toggling .dark and before any paint.
  //     Tells the browser (Chrome, Samsung Internet, Brave, Edge, Safari, Firefox)
  //     what colour palette this document uses so it never injects its own
  //     contrasting colours on top of our CSS variables.
  //     Without this: Samsung Internet auto-dark mode can set text to white on a
  //     light background, or leave text black on a dark background — both invisible.
  root.style.colorScheme = theme.isDark ? 'dark' : 'light';

  // 0b. Dark mode class — drives ALL dark: Tailwind variants.
  //     index.css declares: @custom-variant dark (&:is(.dark *))
  //     Without this toggle, every dark: class in the app is inert.
  root.classList.toggle('dark', theme.isDark);

  // 1. Color tokens
  for (const [key, cssVar] of Object.entries(TOKEN_TO_CSS_VAR) as [keyof typeof TOKEN_TO_CSS_VAR, string][]) {
    root.style.setProperty(cssVar, theme.tokens[key]);
  }

  // 2. Typography tokens (--font-sans and --font-mono override ALL font-mono/font-sans classes)
  const typography = resolveTypography(config.themeId, config.fontStyleId);
  for (const [key, cssVar] of Object.entries(TYPOGRAPHY_CSS_VARS) as [keyof typeof TYPOGRAPHY_CSS_VARS, string][]) {
    root.style.setProperty(cssVar, typography[key]);
  }

  // 3. Corner radius
  const radius = CORNER_RADIUS_MAP[config.cornerStyle];
  root.style.setProperty('--radius-sm', radius.sm);
  root.style.setProperty('--radius-md', radius.md);
  root.style.setProperty('--radius-lg', radius.lg);
  root.style.setProperty('--radius-xl', radius.xl);

  // 4. Font size (on html element, scales rem-based layout)
  root.style.fontSize = FONT_SIZE_MAP[config.fontSize];

  // 5. Animation level (data attribute — CSS rules in index.css target this)
  root.setAttribute('data-animation', config.animationLevel);

  // 6. UI density (data attribute — CSS rules in index.css target this)
  root.setAttribute('data-density', config.density);

  // 7. Load required fonts
  loadFontsForConfig(config);
}

// ─── Context ──────────────────────────────────────────────────────────────────

export interface ThemeContextValue {
  appearance:        AppearanceConfig;
  themeId:           ThemeId;         // shortcut
  theme:             ThemeDefinition; // shortcut
  fontStyleId:       FontStyleId;     // shortcut
  setTheme:          (id: ThemeId) => void;
  setFontStyle:      (id: FontStyleId) => void;
  setFontSize:       (s: FontSize) => void;
  setDensity:        (d: UIDensity) => void;
  setCornerStyle:    (c: CornerStyle) => void;
  setAnimationLevel: (a: AnimationLevel) => void;
}

export const ThemeContext = createContext<ThemeContextValue>({
  appearance:        DEFAULT_APPEARANCE,
  themeId:           DEFAULT_APPEARANCE.themeId,
  theme:             THEMES[DEFAULT_APPEARANCE.themeId],
  fontStyleId:       DEFAULT_APPEARANCE.fontStyleId,
  setTheme:          () => {},
  setFontStyle:      () => {},
  setFontSize:       () => {},
  setDensity:        () => {},
  setCornerStyle:    () => {},
  setAnimationLevel: () => {},
});

// ─── ThemeProvider ────────────────────────────────────────────────────────────

export function ThemeProvider({ children }: { children: ReactNode }) {
  // ── Synchronous first-paint application ─────────────────────────────────
  // applyConfigToDom is called inside the useState lazy initializer so it runs
  // during the React render phase — BEFORE the browser commits and paints.
  //
  // Why this matters: useEffect fires AFTER the first paint. For dark-theme
  // users the anti-FOUC script sets a dark background, but CSS variables still
  // hold light-mode fallback values until useEffect runs. Result: dark
  // background + dark text (--foreground: 220 13% 11%) = invisible text.
  //
  // Moving the call here means CSS vars are correct on the very first frame,
  // eliminating the FOUC for ALL browsers (Chrome, Samsung Internet, Brave,
  // Edge, Safari, Firefox) on both Android and iPhone.
  const [appearance, setAppearance] = useState<AppearanceConfig>(() => {
    const config = loadConfig();
    if (typeof document !== 'undefined') applyConfigToDom(config);
    return config;
  });

  // Safety-net effect: re-applies if React StrictMode double-invokes, or if
  // the component remounts after a hot-reload in development. Idempotent.
  useEffect(() => {
    applyConfigToDom(appearance);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Generic updater — merges partial config, persists, applies
  const update = useCallback((partial: Partial<AppearanceConfig>) => {
    setAppearance(prev => {
      const next = { ...prev, ...partial };
      saveConfig(next);
      applyConfigToDom(next);
      return next;
    });
  }, []);

  const setTheme          = useCallback((id: ThemeId) =>          update({ themeId: id }), [update]);
  const setFontStyle      = useCallback((id: FontStyleId) =>      update({ fontStyleId: id }), [update]);
  const setFontSize       = useCallback((s: FontSize) =>          update({ fontSize: s }), [update]);
  const setDensity        = useCallback((d: UIDensity) =>         update({ density: d }), [update]);
  const setCornerStyle    = useCallback((c: CornerStyle) =>       update({ cornerStyle: c }), [update]);
  const setAnimationLevel = useCallback((a: AnimationLevel) =>    update({ animationLevel: a }), [update]);

  return (
    <ThemeContext.Provider value={{
      appearance,
      themeId:     appearance.themeId,
      theme:       THEMES[appearance.themeId],
      fontStyleId: appearance.fontStyleId,
      setTheme,
      setFontStyle,
      setFontSize,
      setDensity,
      setCornerStyle,
      setAnimationLevel,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}
